import type { FC, MouseEvent, ReactNode } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';

import { DotsVerticalIcon } from '@setup/svg-icons/svg-icons';
import { MuiIconButton, MuiTooltip } from '@shared/ui-kit';

import { ActionMenuBase, ActionMenuBaseItem } from './BaseActionMenu.style';

import type { ActionMenuListModel } from '@shared/models/actionMenuList.model';

type BaseActionMenuProps = {
  menuList: ActionMenuListModel;
  icon?: any;
  isDisabled?: boolean;
  children?: ReactNode;
  isPrimaryHover?: boolean;
  withBackdropFilter?: boolean;
  iconSize?: 'small' | 'medium' | 'large';
  iconColor?: string;
  anchorOriginVertical?: 'bottom' | 'top' | 'center';
  anchorOriginHorizontal?: 'center' | 'right' | 'left';
  transformOriginVertical?: 'bottom' | 'top' | 'center';
  transformOriginHorizontal?: 'center' | 'right' | 'left';
  color?: 'primary' | 'secondary';
  disablePortal?: boolean;
};

export const BaseActionMenu: FC<BaseActionMenuProps> = ({
  menuList,
  icon = <DotsVerticalIcon />,
  withBackdropFilter,
  iconSize = 'small',
  iconColor,
  isDisabled = false,
  anchorOriginVertical = 'bottom',
  anchorOriginHorizontal = 'right',
  transformOriginVertical = 'top',
  transformOriginHorizontal = 'right',
  disablePortal = false,

  children,
}) => {
  const handleClickAwayPopup = (close: () => void, event: MouseEvent) => {
    close();
    event.stopPropagation();
  };
  const theme = useTheme();
  const isSmallDisplay = useMediaQuery(`${theme.breakpoints.down('sm')}`);

  return (
    <PopupState variant="popover" popupId="base-action-menu">
      {(popupState) => (
        <>
          {children ? (
            <Box sx={{ display: 'inline-block', cursor: 'pointer' }} {...bindTrigger(popupState)}>
              {children}
            </Box>
          ) : (
            <MuiIconButton
              isDisabled={isDisabled}
              size={iconSize === 'large' ? 'small' : iconSize}
              withBackdropFilter={withBackdropFilter}
              fillColor={iconColor}
              {...bindTrigger(popupState)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
            </MuiIconButton>
          )}

          <ActionMenuBase
            disablePortal={disablePortal}
            {...bindMenu({
              ...popupState,
              close: (e: MouseEvent) => handleClickAwayPopup(popupState.close, e),
            } as any)}
            anchorOrigin={{
              vertical: anchorOriginVertical,
              horizontal: anchorOriginHorizontal,
            }}
            transformOrigin={{
              vertical: transformOriginVertical,
              horizontal: transformOriginHorizontal,
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {menuList?.map((item, index) =>
              item.isDisabled ? (
                <MuiTooltip
                  key={typeof item.label === 'string' ? item.label : index}
                  placement={isSmallDisplay ? 'top' : 'left'}
                  title={item?.tooltipTitle ? item?.tooltipTitle : ''}
                >
                  <Box onClick={() => (item.disableCallback ? item.disableCallback() : true)}>
                    <ActionMenuBaseItem disabled>
                      {item.startIcon && <item.startIcon />}
                      <Typography variant="t14r" color={theme.palette.case.neutral.n400}>
                        {item?.label}
                      </Typography>
                    </ActionMenuBaseItem>
                  </Box>
                </MuiTooltip>
              ) : (
                <ActionMenuBaseItem
                  key={typeof item.label === 'string' ? item.label : index}
                  onClick={(e) => {
                    popupState.close();
                    e.stopPropagation();
                    item.callback();
                  }}
                >
                  {item.startIcon && <item.startIcon />}
                  <Typography variant="t14r" color={theme.palette.case.neutral.n700}>
                    {item?.label}
                  </Typography>
                </ActionMenuBaseItem>
              ),
            )}
          </ActionMenuBase>
        </>
      )}
    </PopupState>
  );
};
